import {
  APIBlockItem,
  APIMemberData,
  APIVendorComponent,
  ComponentData,
  Media,
} from "@companion-core/shared/app/Interfaces/API";
import { Block } from "@companion-core/shared/app/Components/DynamicPage/dynamicPage.model";
import { getFullBleedBlockTypes } from "@companion-core/shared/app/Utils/config";
import * as countries from "i18n-iso-countries";
import { TFunction } from "i18next";

export const TYPE_TEXT = "text";
export const TYPE_CARD = "card";
export const TYPE_IMAGE = "image";
export const TYPE_BUTTON = "button";
export const TYPE_MEDIA = "media";
export const COMPONENTS_PAGE_SIZE = 15;

export function getComponentsFromBlockItems(blockItem: APIBlockItem[]): ComponentData[] {
  return blockItem.flatMap((result) => result.components.data as ComponentData[]);
}

export function getSortedByOrderComponents(blockItem: APIBlockItem[]): ComponentData[] {
  return blockItem
    .sort((a, b) => a.order - b.order)
    .map((result) => result.components.data as ComponentData[])
    .flat();
}

export function getComponentText(components: ComponentData[]): ComponentData | undefined {
  return components.find((obj) => obj.type === TYPE_TEXT);
}

export function getComponentsCard(components: ComponentData[]): ComponentData[] {
  return components.filter((obj) => obj.type === TYPE_CARD);
}

export function getComponentButton(components: ComponentData[]): ComponentData | undefined {
  return components.find((obj) => obj.type === TYPE_BUTTON);
}

export function getComponentMedia(components: ComponentData[]): ComponentData | undefined {
  return components.find((obj) => obj.type === TYPE_MEDIA);
}

export const getSanitizedDescriptionAlt = (descriptionAlt?: string | null): string | undefined => {
  const altValue = descriptionAlt?.trim();
  if (!altValue) return undefined;
  return altValue.toLowerCase() === "default_alt" ? "" : altValue;
};

export const getResolvedDescriptionAlt = (
  descriptionAlt?: string | null,
  fallback?: string
): string | undefined => {
  if (descriptionAlt?.trim().toLowerCase() === "default_alt") return fallback;
  const sanitizedAlt = getSanitizedDescriptionAlt(descriptionAlt);
  return sanitizedAlt !== undefined ? sanitizedAlt : fallback;
};

/**
 * Get the media URL of a component if exists. Otherwise, get the vendor media URL.
 *
 * @param {ComponentData} component - The component object.
 * @param {string} mediaType - The type of the media we are looking for.
 * @returns {string | undefined} - The media URL if found, otherwise undefined.
 */
export const getContentComponentMediaUrl = (
  component: ComponentData | APIVendorComponent,
  mediaType = "image"
): string | undefined => {
  const componentMedia: Media | undefined = component.medias?.find(
    (media: Media) => media.type === mediaType
  );
  if (componentMedia) return componentMedia.url;
  const vendor = "vendor" in component ? component.vendor : undefined;
  return vendor?.medias?.find((media: Media) => media.type === mediaType)?.url;
};

/**
 * Get the media alt description of a component if exists. Otherwise, get the vendor media alt.
 *
 * @param {ComponentData | APIVendorComponent} component - The component object.
 * @param {string} mediaType - The type of the media we are looking for.
 * @returns {string | undefined} - The media alt description if found, otherwise undefined.
 */
export const getContentComponentMediaDescriptionAlt = (
  component: ComponentData | APIVendorComponent,
  mediaType = "image"
): string | undefined => {
  const componentMedia: Media | undefined = component.medias?.find(
    (media: Media) => media.type === mediaType
  );
  if (componentMedia) return componentMedia.description_alt;
  const vendor = "vendor" in component ? component.vendor : undefined;
  return vendor?.medias?.find((media: Media) => media.type === mediaType)?.description_alt;
};

export function sortData(data: unknown[]) {
  return data.sort((a: unknown, b: unknown) => {
    const aObj = a as Record<string, unknown> & { order_by?: string; order?: number };
    const bObj = b as Record<string, unknown> & { order_by?: string; order?: number };
    const keyA = aObj.order_by ? aObj[aObj.order_by] : aObj.order;
    const keyB = bObj.order_by ? bObj[bObj.order_by] : bObj.order;
    if (keyA != null && keyB != null && keyA < keyB) return -1;
    if (keyA != null && keyB != null && keyA > keyB) return 1;
    return 0;
  });
}

type SortableByOrder<T> = T & { order?: number };

export const sortByOrder = <T>(a?: SortableByOrder<T>, b?: SortableByOrder<T>) => {
  if (a?.order && b?.order) {
    return a?.order - b?.order;
  } else {
    return 0;
  }
};

export const isBlockItemsContainAQuery = (blockItems: APIBlockItem[]) => {
  return blockItems.some((blockItem) => blockItem.type === "query");
};

export const isBlocksContainVerticalList = (blocks: Block[]) =>
  blocks.some((block) => ["list", "extended-mosaic"].includes(block.type));

// Driven by `options.fullBleedBlockTypes`: a full-bleed block paints its own full-screen
// background and applies the safe-area insets itself, so its containers must neither scroll
// nor pad, otherwise the block cannot reach the screen edges.
export const isFullBleedBlock = (blockType: string) => getFullBleedBlockTypes().includes(blockType);

export const isBlocksContainFullBleedBlock = (blocks: Block[]) =>
  blocks.some((block) => isFullBleedBlock(block.type));

const SUPPORTED_DYNAMIC_PLACEHOLDERS_REGEX = /\{(?:first-name|last-name|salutation|city|country)\}/;

export const containsSupportedDynamicPlaceholder = (value: string) =>
  SUPPORTED_DYNAMIC_PLACEHOLDERS_REGEX.test(value);

/**
 * Get the translated salutation text for a member.
 *
 * @param {string | null} salutation - The salutation key (e.g. "Mr", "Mrs", "Ms").
 * @param {TFunction} t - The translation function.
 * @returns {string} - The translated salutation, or empty string if unknown.
 */
export const getSalutationText = (salutation: string | null, t: TFunction) => {
  switch (salutation) {
    case "Mr.":
      return t("common.salutation.mr");
    case "Mrs.":
      return t("common.salutation.mrs");
    case "Ms.":
      return t("common.salutation.ms");
    case "Mx.":
      return t("common.salutation.mx");
    default:
      return "";
  }
};

/**
 * Replaces a placeholder with the provided value if it is not empty.
 * Otherwise, it removes the placeholder along with a single preceding space to avoid leaving unnecessary whitespace.
 * @param {string} content - The content to replace the placeholder in.
 * @param {string} placeholder - The placeholder to replace.
 * @param {string} value - The value to replace the placeholder with.
 * @returns {string} - The content with the placeholder replaced or removed.
 */
export const replacePlaceholder = (content: string, placeholder: string, value?: string | null) => {
  const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const placeholderRegex = new RegExp(escapedPlaceholder, "g");
  if (value) {
    return content.replace(placeholderRegex, value);
  }
  return content
    .replace(new RegExp(` ${escapedPlaceholder}`, "g"), "")
    .replace(placeholderRegex, "");
};

/**
 * Personnalize the dynamic content with the member variables.
 *
 * @param {string} dynamicContent - The dynamic content to personnalize.
 * @param {Pick<APIMemberData, "firstname" | "lastname" | "addresses" | "salutation">} memberVars - The member variables.
 * @param {TFunction} t - The translation function.
 * @param {string} language - The language of the content.
 * @returns {string} - The personnalized dynamic content.
 */
export const PersonnalizeDynamicContent = (
  dynamicContent: string,
  memberVars: Pick<APIMemberData, "firstname" | "lastname" | "addresses" | "salutation">,
  t: TFunction,
  language: string
) => {
  const { firstname, lastname, addresses, salutation } = memberVars;
  const { city, country_code: countryCode } = addresses?.[0] ?? {};
  const country = countries.getName(countryCode, language);
  const salutationText = getSalutationText(salutation, t);
  return replacePlaceholder(
    replacePlaceholder(
      replacePlaceholder(
        replacePlaceholder(
          replacePlaceholder(dynamicContent, "{first-name}", firstname),
          "{last-name}",
          lastname
        ),
        "{city}",
        city
      ),
      "{country}",
      country
    ),
    "{salutation}",
    salutationText
  );
};

/**
 * Recursively personnalize all string fields containing placeholders in an object/array tree.
 *
 * @param {unknown} value - Any value (string/object/array/primitive).
 * @param {Pick<APIMemberData, "firstname" | "lastname" | "addresses" | "salutation">} memberVars - Member variables for replacement.
 * @param {TFunction} t - Translation function.
 * @param {string} language - Language used for country translation.
 * @returns {unknown} - Same structure with placeholders replaced in string fields.
 */
export const personnalizeObjectStringFields = (
  value: unknown,
  memberVars: Pick<APIMemberData, "firstname" | "lastname" | "addresses" | "salutation">,
  t: TFunction,
  language: string
): unknown => {
  if (typeof value === "string") {
    if (!containsSupportedDynamicPlaceholder(value)) return value;
    return PersonnalizeDynamicContent(value, memberVars, t, language);
  }

  if (Array.isArray(value)) {
    return value.map((item) => personnalizeObjectStringFields(item, memberVars, t, language));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        personnalizeObjectStringFields(nestedValue, memberVars, t, language),
      ])
    );
  }

  return value;
};
